import { SolutionScenario } from '@/types/scenarios/solution';
import { Home, Phone, Calendar, Users, MapPin, TrendingUp, Key, Building2 } from 'lucide-react';

export const realEstate: SolutionScenario = {
  meta: {
    id: 'real-estate',
    slug: 'real-estate',
    name: 'Real Estate',
    tagline: 'Genies that handle every listing, tenant, and property question',
    description: "A genie that handles listing inquiries, tenant maintenance calls, and the same property questions every day so your agents and managers can run the deals, not the call centre.",
    market: 'Real Estate',
    subMarkets: ['Residential Agents', 'Commercial Real Estate', 'Property Management', 'Real Estate Teams', 'Brokerages'],
    specificNeeds: [
      'Listing inquiry capture',
      'Property showing scheduling',
      'Buyer & seller qualification',
      'Open house information',
      'Market updates & pricing',
      'After hours lead capture'
    ],
    commonRoles: ['Real Estate Agents', 'Brokers', 'Property Managers', 'Transaction Coordinators'],
    marketSize: '$2.5 trillion US real estate market',
    isFeatured: true
  },

  hero: {
    headline: 'Every property question, answered',
    headlineGradient: 'Every property question,',
    headlineRest: 'answered',
    subheadline: "A genie that handles listing inquiries, tenant maintenance calls, and the same property questions every day so your agents and managers can run the deals, not the call centre.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'interactive-faq',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'listing-inquiries',
      name: 'Listing Inquiry Capture',
      description: 'Capture every property inquiry instantly — day or night — with detailed caller information and property interest',
      department: 'Sales',
      icon: Home,
      playbookIds: ['lead-capture', 'interactive-faq'],
      benefits: [
        '24/7 listing inquiry capture',
        'Property-specific details provided',
        'Caller contact info collected',
        'Instant agent notification'
      ]
    },
    {
      id: 'showing-scheduling',
      name: 'Showing Scheduling',
      description: 'Book property viewings automatically with calendar integration and confirmation',
      department: 'Sales',
      icon: Calendar,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Real-time availability checking',
        'Automatic showing confirmations',
        'Reschedule and cancel handling',
        'Multi-property tour booking'
      ]
    },
    {
      id: 'buyer-qualification',
      name: 'Buyer Qualification',
      description: 'Pre-qualify buyers with key questions about budget, timeline, financing, and preferences',
      department: 'Sales',
      icon: Users,
      playbookIds: ['lead-capture', 'interactive-faq'],
      benefits: [
        'Budget and timeline screening',
        'Pre-approval status check',
        'Property preference capture',
        'Hot lead prioritization'
      ]
    },
    {
      id: 'property-management',
      name: 'Property Management Support',
      description: 'Handle tenant inquiries, maintenance requests, and property updates around the clock',
      department: 'Operations',
      icon: Building2,
      playbookIds: ['interactive-faq', 'appointment-scheduler'],
      benefits: [
        'Maintenance request intake',
        'Tenant inquiry handling',
        'Emergency escalation',
        'Lease renewal reminders'
      ]
    },
    {
      id: 'open-house',
      name: 'Open House Support',
      description: 'Answer pre and post open house questions, capture visitor interest, and follow up automatically',
      department: 'Marketing',
      icon: Key,
      playbookIds: ['lead-capture', 'proactive-welcome'],
      benefits: [
        'Open house details on demand',
        'Post-visit follow-up capture',
        'Visitor interest tracking',
        'Neighborhood information'
      ]
    },
    {
      id: 'market-updates',
      name: 'Market Updates & CMA',
      description: 'Provide market data, comparable sales, and pricing information to buyers and sellers',
      department: 'Sales',
      icon: TrendingUp,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Neighborhood market trends',
        'Comparable sales information',
        'Days on market data',
        'Pricing guidance'
      ]
    }
  ],

  challenges: [
    {
      title: 'Missed Listing Inquiries',
      description: 'Buyers call about properties at all hours — evenings, weekends, and during showings when agents can\'t answer',
      impact: 'high',
      affectedAreas: ['Lead capture', 'Revenue', 'Client acquisition']
    },
    {
      title: 'Showing Coordination Overload',
      description: 'Scheduling, rescheduling, and confirming property showings consumes hours of agent time daily',
      impact: 'high',
      affectedAreas: ['Agent productivity', 'Client experience', 'Time management']
    },
    {
      title: 'Unqualified Lead Follow-Up',
      description: 'Agents waste time on leads that aren\'t pre-approved, aren\'t serious, or don\'t match available inventory',
      impact: 'high',
      affectedAreas: ['Sales efficiency', 'Agent burnout', 'Conversion rates']
    },
    {
      title: 'After Hours Availability Gap',
      description: 'Most property searches happen evenings and weekends — exactly when agents are unavailable',
      impact: 'high',
      affectedAreas: ['Lead loss', 'Competitor advantage', 'Client satisfaction']
    }
  ],

  solutions: [
    {
      challenge: 'Missed listing inquiries during showings and after hours',
      solution: '24/7 Voice Genie that captures every listing inquiry with property details and caller information',
      implementation: 'Deploy as primary after hours line and overflow during showings',
      expectedResults: [
        '45% increase in captured listing leads',
        'Zero missed inquiries after hours',
        'Instant agent notification for hot leads'
      ]
    },
    {
      challenge: 'Time-consuming showing coordination',
      solution: 'Automated showing scheduling with calendar integration and smart confirmation',
      implementation: 'Voice Genie checks agent availability, books showings, sends confirmations, and handles reschedules',
      expectedResults: [
        '3+ hours saved per agent per day',
        '60% reduction in scheduling back-and-forth',
        'Automated showing reminders reduce no-shows'
      ]
    },
    {
      challenge: 'Wasted time on unqualified leads',
      solution: 'Voice Genie pre-qualifies every inquiry with budget, timeline, and financing questions',
      implementation: 'Intelligent qualification flow that prioritizes hot leads and flags pre-approved buyers',
      expectedResults: [
        '40% improvement in lead quality',
        'Agents focus only on qualified buyers',
        'Faster time to close'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'Follow Up Boss',
        category: 'CRM',
        description: 'Real estate CRM for lead management and follow-up',
        required: false,
        popularityScore: 92
      },
      {
        name: 'kvCORE',
        category: 'Platform',
        description: 'Real estate lead generation and management platform',
        required: false,
        popularityScore: 88
      },
      {
        name: 'BoomTown',
        category: 'CRM',
        description: 'Real estate lead generation and CRM',
        required: false,
        popularityScore: 85
      },
      {
        name: 'Zillow Premier Agent',
        category: 'Lead Source',
        description: 'Zillow and MLS listing lead integration',
        required: false,
        popularityScore: 95
      }
    ],
    common: [
      {
        name: 'Google Calendar',
        category: 'Scheduling',
        description: 'Calendar integration for showing scheduling',
        required: false,
        popularityScore: 95
      },
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Customer relationship management',
        required: false,
        popularityScore: 90
      }
    ]
  },

  metrics: {
    averageImplementationTime: '1-2 weeks',
    typicalROI: '350%',
    adoptionRate: '90% agent satisfaction',
    customerSatisfaction: '4.8/5'
  },

  demoConfig: {
    firstMessage: "Hi there! I'm your real estate assistant. I can help you with property listings, schedule showings, answer questions about the market, or connect you with an agent. How can I help you today?",
    systemPrompt: "You are a knowledgeable and friendly real estate assistant. Help callers with property listing inquiries, schedule showings, answer market questions, and qualify buyer interest. Collect contact information naturally. Be warm and professional — you represent the agent's brand. When someone asks about a specific property, provide details and offer to schedule a showing.",
    marketContext: "Real estate agency or brokerage providing listing and buyer services",
    exampleScenarios: [
      "I saw a listing on Zillow for 123 Oak Street — is it still available?",
      "I'd like to schedule a showing this weekend",
      "What's the market like in the downtown area?",
      "We're looking to sell our home — what would it be worth?"
    ]
  },

  caseStudies: [
    {
      companyName: 'Pinnacle Real Estate Group',
      companySize: '35 agents',
      location: 'Phoenix, Arizona',
      challenge: 'Agents missing 40%+ of listing inquiries during showings and after hours, losing leads to competitors who responded first',
      implementation: 'Deployed Help Genie as the team\'s after hours and overflow line, with automatic lead qualification and showing scheduling',
      timeline: '10 days',
      results: [
        {
          metric: 'Lead Capture Rate',
          value: '+52%',
          improvement: 'More leads captured'
        },
        {
          metric: 'Showing Bookings',
          value: '+38%',
          improvement: 'Automated scheduling'
        },
        {
          metric: 'Agent Time Saved',
          value: '3.5 hrs/day',
          improvement: 'Per agent average'
        },
        {
          metric: 'Speed to Lead',
          value: 'Instant',
          improvement: 'From 4+ hour average'
        }
      ],
      quote: {
        text: "We were losing leads every single day to agents who picked up the phone first. Now our Genie captures every inquiry instantly, qualifies the buyer, and books the showing before I even see the notification. Our conversion rate has never been higher.",
        author: 'Rachel Kim',
        title: 'Team Lead & Broker'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Real Estate Voice Lead Capture Guide',
      description: 'How top-producing agents use voice AI to capture and convert more listing leads',
      url: '/resources/real-estate-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Never Miss a Listing Lead Again',
      description: 'How real estate teams are using Help Genie to dominate speed-to-lead',
      url: '/webinars/real-estate-lead-capture'
    },
    {
      type: 'template',
      title: 'Real Estate Qualification Scripts',
      description: 'Pre-built voice scripts for buyer qualification, showing scheduling, and listing inquiries',
      url: '/templates/real-estate-scripts'
    }
  ],

  faqs: [
    {
      question: 'Can the Genie answer questions about specific listings?',
      answer: 'Yes. You can upload your listing details to the knowledge base and the Genie will provide accurate property information — bedrooms, bathrooms, price, square footage, features, and more — for every listing you have.'
    },
    {
      question: 'Does it integrate with my MLS or CRM?',
      answer: 'Help Genie integrates with popular real estate platforms including Follow Up Boss, kvCORE, BoomTown, and Salesforce. Leads are automatically pushed to your CRM with full qualification details.'
    },
    {
      question: 'Can buyers schedule showings directly through the Genie?',
      answer: 'Absolutely. The Genie checks your calendar availability, books the showing, sends confirmation to both you and the buyer, and even sends reminders to reduce no-shows. It handles reschedules and cancellations too.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "Every listing call answered\nevery buyer qualified",
      subtitle: "See how your Genie handles real estate inquiries — capturing leads, scheduling showings, and qualifying buyers while you're at a closing or showing another property.",
    },
    chatScenarios: [
      {
        id: 'listing_inquiry',
        title: 'Listing Inquiry',
        messages: [
          { type: 'user' as const, text: "Hi, I saw the listing at 742 Evergreen Terrace — the four-bedroom with the pool. Is it still available?", delay: 300 },
          { type: 'genie' as const, text: "Yes, 742 Evergreen Terrace is still active! It's a beautiful 4-bed, 3-bath home with 2,800 square feet, listed at $485,000. The pool was resurfaced last year, and it has a new roof. Would you like to schedule a showing?", delay: 1200 },
          { type: 'user' as const, text: "That sounds great. What's the HOA situation? And are there any other offers?", delay: 2500 },
          { type: 'genie' as const, text: "The HOA is $175 per month and covers landscaping, pool maintenance for common areas, and the community gym. As for offers, I'd recommend scheduling a showing soon — there's been strong interest this week. What days work for you?", delay: 3400 },
          { type: 'user' as const, text: "Saturday morning would be perfect. My name is David Chen.", delay: 4600 },
          { type: 'genie' as const, text: "I've scheduled a showing at 742 Evergreen Terrace for Saturday at 10 AM for David Chen. I'll send you a confirmation text with the address and agent details. Can I grab your phone number and email?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Property details provided instantly', delay: 6500 },
          { id: '2', text: 'HOA and market context shared', delay: 6700 },
          { id: '3', text: 'Showing scheduled for Saturday', delay: 6900 },
          { id: '4', text: 'Lead contact info captured', delay: 7100 },
          { id: '5', text: 'Agent notified of hot lead', delay: 7300 },
        ],
      },
      {
        id: 'showing_scheduling',
        title: 'Showing Request',
        messages: [
          { type: 'user' as const, text: "We'd like to see a few homes this weekend. We're pre-approved up to $600K and looking in the Westside area.", delay: 300 },
          { type: 'genie' as const, text: "That's a great area — we have several listings in Westside within your range. I can set up a tour of 3-4 homes for you. Do you prefer Saturday or Sunday?", delay: 1200 },
          { type: 'user' as const, text: "Saturday afternoon works. We need at least 3 bedrooms and a two-car garage.", delay: 2500 },
          { type: 'genie' as const, text: "I've found four properties matching your criteria in Westside — all 3+ bedrooms with two-car garages, ranging from $489K to $579K. I can schedule back-to-back showings starting at 1 PM Saturday. Sound good?", delay: 3400 },
          { type: 'user' as const, text: "Perfect. We're the Martinez family — Tom and Lisa.", delay: 4600 },
          { type: 'genie' as const, text: "All set! I've booked four showings for Tom and Lisa Martinez, Saturday starting at 1 PM. You'll receive a text with the tour route and property details for each home. Our agent Sarah will meet you at the first property.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Buyer pre-qualification confirmed', delay: 6500 },
          { id: '2', text: 'Property criteria matched (4 homes)', delay: 6700 },
          { id: '3', text: 'Multi-property tour scheduled', delay: 6900 },
          { id: '4', text: 'Agent assigned and notified', delay: 7100 },
          { id: '5', text: 'Tour details sent via text', delay: 7300 },
        ],
      },
      {
        id: 'buyer_qualification',
        title: 'Buyer Qualification',
        messages: [
          { type: 'user' as const, text: "We're thinking about buying our first home. Not really sure where to start.", delay: 300 },
          { type: 'genie' as const, text: "Congratulations on taking that first step! I'd love to help you get started. Have you had a chance to get pre-approved for a mortgage yet? That's usually the best first move.", delay: 1200 },
          { type: 'user' as const, text: "Not yet. We've been saving for a down payment — we have about $40K saved up.", delay: 2500 },
          { type: 'genie' as const, text: "That's a solid start! With $40K down, you'd be looking at a comfortable range depending on the loan type. What's your combined household income, roughly? And are you looking to stay in a specific school district or neighborhood?", delay: 3400 },
          { type: 'user' as const, text: "Around $95K combined. We'd love to be near Lincoln Elementary if possible.", delay: 4600 },
          { type: 'genie' as const, text: "The Lincoln Elementary area has some great options in your estimated range. I'd recommend connecting with our agent who specializes in first-time buyers — she can walk you through pre-approval and start showing you homes. Can I have her call you tomorrow?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'First-time buyer identified', delay: 6500 },
          { id: '2', text: 'Financial snapshot captured', delay: 6700 },
          { id: '3', text: 'Location preference noted', delay: 6900 },
          { id: '4', text: 'Matched with specialist agent', delay: 7100 },
          { id: '5', text: 'Follow-up callback scheduled', delay: 7300 },
        ],
      },
      {
        id: 'after_hours_lead',
        title: 'After Hours',
        messages: [
          { type: 'user' as const, text: "I know it's late, but I just saw a listing online at 55 Harbor View — it says 'coming soon.' Can I get any details?", delay: 300 },
          { type: 'genie' as const, text: "No worries — I'm here 24/7! 55 Harbor View is a stunning 5-bed, 4-bath waterfront property coming to market this week at $1.2M. It features a private dock, updated kitchen, and panoramic bay views. Would you like to be first on the showing list?", delay: 1200 },
          { type: 'user' as const, text: "Absolutely. That's exactly what we've been looking for. When can we see it?", delay: 2500 },
          { type: 'genie' as const, text: "I'll put you at the top of the list. The first showings are expected Thursday. I'll have our waterfront specialist reach out to you first thing in the morning to arrange a private preview. What's your name and best contact number?", delay: 3400 },
          { type: 'user' as const, text: "James Whitfield, 555-0192. We're pre-approved up to $1.5M with First National.", delay: 4600 },
          { type: 'genie' as const, text: "Excellent, James. You're confirmed for priority access to 55 Harbor View. I've noted your pre-approval with First National. Our agent Michael will call you by 9 AM tomorrow to schedule your private showing. This one won't last long!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'After hours inquiry captured (11 PM)', delay: 6500 },
          { id: '2', text: 'Coming-soon listing details provided', delay: 6700 },
          { id: '3', text: 'Priority showing list placement', delay: 6900 },
          { id: '4', text: 'Pre-approval verified ($1.5M)', delay: 7100 },
          { id: '5', text: 'Agent callback scheduled for 9 AM', delay: 7300 },
        ],
      },
      {
        id: 'property_management',
        title: 'Tenant Support',
        messages: [
          { type: 'user' as const, text: "Hi, I'm a tenant at Oakwood Apartments, unit 312. My kitchen sink has been leaking since this afternoon.", delay: 300 },
          { type: 'genie' as const, text: "I'm sorry about that. Let me help get this resolved. Is the leak coming from the faucet or from under the sink? And how severe is it — a drip or a steady flow?", delay: 1200 },
          { type: 'user' as const, text: "It's under the sink, a steady drip. I've put a bucket under it but it fills up in a few hours.", delay: 2500 },
          { type: 'genie' as const, text: "Good thinking with the bucket. This sounds like a pipe connection issue. I've created a maintenance request for unit 312 — plumbing, under-sink leak. Our maintenance team will be out tomorrow morning. In the meantime, you can turn off the water valve under the sink to stop the drip.", delay: 3400 },
          { type: 'user' as const, text: "Oh I see the valve. That stopped it. Thanks! Will someone come tomorrow for sure?", delay: 4600 },
          { type: 'genie' as const, text: "Yes — you're confirmed for a maintenance visit tomorrow between 8 AM and noon. You'll receive a text when the technician is en route. Is there anything else I can help with for unit 312?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Maintenance request created', delay: 6500 },
          { id: '2', text: 'Issue severity assessed', delay: 6700 },
          { id: '3', text: 'Interim fix guided (shut-off valve)', delay: 6900 },
          { id: '4', text: 'Service visit scheduled', delay: 7100 },
          { id: '5', text: 'Tenant confirmation sent', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Stop Losing Leads to Voicemail",
      subtitle: "In real estate, the first agent to respond wins the client. Your Genie answers every call, qualifies every buyer, and books every showing — even at midnight.",
    },
    useCaseCards: [
      {
        id: 'lead-capture',
        title: 'Listing Lead Capture',
        description: "Buyers call about listings at 9 PM, during open houses, and while you're at closings. Your Genie captures every inquiry with property details and contact info.",
        bullets: [
          'Answers listing questions with property details',
          'Captures caller name, number, and email',
          'Notifies agent instantly for hot leads',
          'Zero inquiries lost to voicemail',
        ],
        image: '/solutions-pages/real-estate/usecase-listing-leads.webp',
      },
      {
        id: 'showing-coordination',
        title: 'Showing Scheduling',
        description: "No more phone tag. Your Genie checks your calendar, books showings, sends confirmations, and handles reschedules — all without you lifting a finger.",
        bullets: [
          'Real-time calendar availability',
          'Multi-property tour scheduling',
          'Automatic confirmations and reminders',
          'Reschedule and cancellation handling',
        ],
        image: '/solutions-pages/real-estate/usecase-showing-scheduling.webp',
      },
      {
        id: 'buyer-seller-qualification',
        title: 'Buyer Qualification',
        description: "Your Genie asks the right questions — budget, timeline, pre-approval, preferences — so you only spend time with serious, qualified buyers.",
        bullets: [
          'Pre-approval status verification',
          'Budget and timeline screening',
          'Property preference capture',
          'Hot lead prioritization and scoring',
        ],
        image: '/solutions-pages/real-estate/usecase-buyer-qualification.webp',
      },
      {
        id: 'property-management',
        title: 'Property Management',
        description: "Tenants need help at all hours. Your Genie handles maintenance requests, answers lease questions, and escalates emergencies — keeping tenants happy and your phone quiet.",
        bullets: [
          'Maintenance request intake and routing',
          'Lease and payment inquiries',
          'Emergency escalation protocols',
          'Tenant satisfaction improvement',
        ],
        image: '/solutions-pages/real-estate/usecase-property-management.webp',
      },
    ],
    finalCTA: {
      title: "The First Agent to Respond Wins",
      subtitle: "At 10 PM when a buyer falls in love with a listing, at 7 AM when a seller wants a market update, during your busiest showing day — your Genie is always there, always closing.",
      pillars: [
        {
          title: 'Always\nCapturing',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Every listing inquiry answered, every buyer contact captured — 24/7.',
        },
        {
          title: 'Always\nQualifying',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Budget, timeline, pre-approval — only qualified leads reach your phone.',
        },
        {
          title: 'Always\nClosing',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Showings booked, follow-ups scheduled, deals moving forward around the clock.',
        },
      ],
      backgroundImage: '/solutions-pages/real-estate/cta-background.webp',
    },
  }
};
